/* Renders Login page */

import React, { useState, useEffect } from 'react';
import { Redirect, Link } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Typography, Box, withTheme } from '@material-ui/core';
import { PrimaryButton } from '../utilities/commonUtilities/GenericButtonElements';
import { computeMinutes } from '../utilities/commonUtilities/dateTools';
import { PasswordInput, EmailInput } from '../utilities/commonUtilities/FormElements';
import { PATH_HOME, PATH_ONE_PLUS_AUTHENTICATION } from '../../containers/RoutePaths';
import { LoginStyle } from '../../styles/LoginStyle';
import clsx from 'clsx';

const PasswordResetLink = withTheme(styled(Link)`
    text-decoration: none;
    cursor: pointer;
    ${({ theme }) => `
    &:link {
        color: ${theme.palette.primary.main};
    }
    &:visited {
        color: ${theme.palette.primary.main};
    }
    &:hover {
        color: ${theme.palette.primary.main};
    }
    &:active {
        color: ${theme.palette.primary.main};
    }
    `}
    ${props => props.theme.breakpoints.down("md")} {
        margin-top: auto;
    }
`);

const CenteredText = styled(Typography)`
    display: flex;
    justify-content: center;
`;

const UserField = styled(EmailInput)`
    margin-bottom: 16px;
`;

const PasswordField = styled(PasswordInput)`
    margin-bottom: 16px;
`;

const TextError = styled(CenteredText)`
    margin-bottom: 16px;
`;

const LoginButton = styled(PrimaryButton)`
    margin-bottom: 16px;
	box-shadow: none;
`;

const TextForgot = styled(CenteredText)``;

const failureCodes = {
    LOCKED_ACCOUNT: 'LOCKED_ACCOUNT',
};

const generateLockedOutMsg = (lockedUntilInMillis, serverTimeInMillis) => {
    const diffInMinutes = computeMinutes(lockedUntilInMillis, serverTimeInMillis);
    const unit = diffInMinutes === 1 ? 'minute' : 'minutes';

    return `You have reached the maximum number of sign-in attempts. Please try again in ${diffInMinutes} ${unit}.`;
};

const LoginForm = ({ hasAuthenticated, token, onSubmit, isNetworkError, failureCode, serverTimeInMillis, lockedUntil, isDeviceTokenRequired }) => {
    const INVALID_LOGIN_STRING = 'Incorrect username or password. Try again';
    const NETWORK_ERROR_STRING = 'Something went wrong with login';
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [disableOnSubmit, setDisableOnSubmit] = useState(false);

    const classes = LoginStyle();

    useEffect(() => {
        if (hasAuthenticated && token === '') {
            // set error message
            if (isNetworkError) {
                setErrorMessage(NETWORK_ERROR_STRING);
            } else if (failureCode === failureCodes.LOCKED_ACCOUNT) {
                setErrorMessage(generateLockedOutMsg(lockedUntil, serverTimeInMillis));
            } else {
                setErrorMessage(INVALID_LOGIN_STRING);
            }
            setDisableOnSubmit(false);
        }
    }, [hasAuthenticated, token, isNetworkError, failureCode, lockedUntil, serverTimeInMillis]);

    const handleSubmit = e => {
        e.preventDefault();
        setDisableOnSubmit(true); // disable login button on submit
        setErrorMessage(''); //clear error message
        onSubmit(username, password);
    };

    const loginButtonEnabled = () => {
        if (disableOnSubmit || username === '' || password === '') {
            return false;
        }
        return true;
    };

    return (
        <form onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="column" width="100%">
                <UserField
                    inputProps={{ 'data-cy': 'text_username' }}
                    className={clsx(classes.loginField, { [classes.textFilled]: username })}
                    name="username"
                    placeholder="Email"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    adornStart
                />
                <PasswordField
                    inputProps={{ 'data-cy': 'text_password' }}
                    className={clsx(classes.loginField, { [classes.textFilled]: password })}
                    name="password"
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    adornStart
                    adornEnd
                />
                {token === '' ? (
                    <TextError data-cy="label_error" variant="caption" color="error">
                        {errorMessage}
                    </TextError>
                ) : isDeviceTokenRequired === 'true' ? (
                    <Redirect to={PATH_ONE_PLUS_AUTHENTICATION} />
                ) : (
                    <Redirect to={PATH_HOME} />
                )}
                <LoginButton data-cy="button_submit" type="submit" disabled={!loginButtonEnabled()}>
                    Sign In
                </LoginButton>
                <PasswordResetLink data-cy="link_forgotpassword" to="/patient/login/requestPasswordReset">
                    <TextForgot variant="caption">Forgot your password?</TextForgot>
                </PasswordResetLink>
            </Box>
        </form>
    );
};

/* type checking for props */
LoginForm.propTypes = {
    token: PropTypes.string.isRequired,
    hasAuthenticated: PropTypes.bool.isRequired,
    isNetworkError: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

export default LoginForm;
